import type { StateCreator } from 'zustand';
import {
  linkedinWurfApi,
  type Urteil,
  type WurfKarten,
  type WurfPost,
  type WurfSlot,
} from '../services/linkedinWurfApi';

/** Schritt "Text" in LinkedIn Neu (ADR 0290). Eigener Slice, damit der alte Cockpit-Zustand unberührt bleibt. */

export type StudioArbeit = 'wuerfeln' | 'tauschen' | 'schreiben' | 'urteilen' | null;

export interface StudioWurf {
  karten: WurfKarten;
  form: string;
  leereFaecher: string[];
}

export interface LinkedInStudioSlice {
  studioStrategyId: number | null;
  studioPosttag: string;
  studioWurf: StudioWurf | null;
  studioPost: WurfPost | null;
  studioUrteil: Urteil | null;
  studioArbeit: StudioArbeit;
  studioFehler: string | null;

  studioSetStrategy: (id: number | null) => void;
  studioSetPosttag: (tag: string) => void;
  studioWuerfeln: () => Promise<void>;
  studioTauschen: (slot: WurfSlot) => Promise<void>;
  studioSchreiben: () => Promise<void>;
  studioUrteilen: (rating: Urteil, notes?: string) => Promise<void>;
}

function lokalesDatum(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const t = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${t}`;
}

/** Morgen, oder der Montag danach, wenn morgen ein Wochenende ist. */
export function naechsterWerktag(heute: Date = new Date()): string {
  const d = new Date(heute);
  d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return lokalesDatum(d);
}

function fehlertext(e: unknown): string {
  return e instanceof Error && e.message ? e.message : 'Unbekannter Fehler';
}

export const createLinkedInStudioSlice: StateCreator<LinkedInStudioSlice, [], [], LinkedInStudioSlice> = (set, get) => ({
  studioStrategyId: null,
  studioPosttag: naechsterWerktag(),
  studioWurf: null,
  studioPost: null,
  studioUrteil: null,
  studioArbeit: null,
  studioFehler: null,

  // Eine andere Strategie hat andere Karten: Wurf und Post der alten gelten nicht mehr.
  studioSetStrategy: (id) =>
    set({ studioStrategyId: id, studioWurf: null, studioPost: null, studioUrteil: null, studioFehler: null }),
  studioSetPosttag: (tag) => set({ studioPosttag: tag }),

  studioWuerfeln: async () => {
    const { studioStrategyId, studioPosttag, studioArbeit } = get();
    if (studioArbeit || studioStrategyId == null) return; // Doppelklick und fehlende Strategie
    set({ studioArbeit: 'wuerfeln', studioFehler: null });
    try {
      const r = await linkedinWurfApi.wuerfeln(studioStrategyId, studioPosttag);
      set({
        studioWurf: { karten: r.karten, form: r.form, leereFaecher: r.leere_faecher },
        studioPost: null,
        studioUrteil: null,
        studioArbeit: null,
      });
    } catch (e) {
      set({ studioArbeit: null, studioFehler: fehlertext(e) });
    }
  },

  studioTauschen: async (slot) => {
    const { studioStrategyId, studioPosttag, studioWurf, studioArbeit } = get();
    if (studioArbeit || studioStrategyId == null || !studioWurf) return;
    set({ studioArbeit: 'tauschen', studioFehler: null });
    try {
      const alt = studioWurf.karten[slot];
      const r = await linkedinWurfApi.tauschen(studioStrategyId, studioPosttag, slot, alt ? [alt.id] : []);
      const karten = { ...studioWurf.karten, [slot]: r.karte };
      // Die Form hängt an den Karten (Fallstudie, Einwand); der Server korrigiert sie beim Schreiben noch einmal.
      set({ studioWurf: { ...studioWurf, karten }, studioPost: null, studioUrteil: null, studioArbeit: null });
    } catch (e) {
      set({ studioArbeit: null, studioFehler: fehlertext(e) });
    }
  },

  studioSchreiben: async () => {
    const { studioStrategyId, studioPosttag, studioWurf, studioArbeit } = get();
    if (studioArbeit || studioStrategyId == null || !studioWurf) return;
    set({ studioArbeit: 'schreiben', studioFehler: null, studioUrteil: null });
    try {
      const post = await linkedinWurfApi.schreiben(studioStrategyId, studioPosttag, studioWurf.karten, studioWurf.form);
      set({ studioPost: post, studioArbeit: null });
    } catch (e) {
      set({ studioArbeit: null, studioFehler: fehlertext(e) });
    }
  },

  studioUrteilen: async (rating, notes) => {
    const { studioPost, studioArbeit } = get();
    if (studioArbeit || !studioPost) return;
    set({ studioArbeit: 'urteilen', studioFehler: null });
    try {
      await linkedinWurfApi.urteilen(studioPost.generation_id, rating, notes);
      set({ studioUrteil: rating, studioArbeit: null });
    } catch (e) {
      set({ studioArbeit: null, studioFehler: fehlertext(e) });
    }
  },
});
